<template>
  <div class="flex flex-col gap-[32px] mt-20 ">
    <!-- Heading Section -->
    <PageHeading 
      title="Organization Information" 
      subtitle="Share key details to strengthen your fundraising foundation." 
    />

    <!-- Form Section -->
    <div class="max-w-lg flex flex-col gap-[20px] h-[calc(100vh-356px)] overflow-y-auto pr-4">
      <!-- Logo Upload -->
      <FormLogoUpload 
        v-model="form.logo" 
        label="Upload Organization Logo" 
        required 
        :placeholder="logoDisplay" 
        :errorMessage="shouldShowError(v$.logo) ? v$.logo.$errors[0].$message : ''" 
      />

      <!-- Organization Details -->
      <FormInput label="Organization Name" v-model="form.name" required placeholder="Enter your organization name" :errorMessage="shouldShowError(v$.name) ? v$.name.$errors[0].$message : ''" @input="v$.name.$touch()" />
      <FormInput label="Organization Address" v-model="form.address" required placeholder="Enter address" :errorMessage="shouldShowError(v$.address) ? v$.address.$errors[0].$message : ''" @input="v$.address.$touch()" />
      <FormInput label="Organization Website" v-model="form.website" required placeholder="Enter website" :errorMessage="shouldShowError(v$.website) ? v$.website.$errors[0].$message : ''" @input="v$.website.$touch()" />

      <!-- Phone Number -->
      <FormPhoneInput 
        label="Organization Phone Number" 
        v-model="form.phone" 
        required 
        :defaultCountry="form.phoneCountry" 
        :errorMessage="shouldShowError(v$.phone) ? v$.phone.$errors[0].$message : ''" 
        @validate="handlePhoneValidation"
        @country-changed="handleCountryChange"
        @blur="fieldTouched.phone = true"
      />

      <!-- Colors -->
      <FormColorPicker label="Choose Primary Color" v-model="form.primaryColor" required :errorMessage="shouldShowError(v$.primaryColor) ? v$.primaryColor.$errors[0].$message : ''" @input="v$.primaryColor.$touch()" />
      <FormColorPicker label="Choose Secondary Color" v-model="form.secondaryColor" required :errorMessage="shouldShowError(v$.secondaryColor) ? v$.secondaryColor.$errors[0].$message : ''" @input="v$.secondaryColor.$touch()" />
      
      <!-- Font Selection -->
      <FormSelect label="Font Family" v-model="form.font" required :options="fonts" :errorMessage="shouldShowError(v$.font) ? v$.font.$errors[0].$message : ''" @input="v$.font.$touch()" />
      
      <!-- Submit Button -->
      <div class="w-full h-[48px] flex items-center justify-center">
        <NextButton 
          text="Next" 
          nextRoute="about-you" 
          :validateForm="true" 
          :v$="v$" 
          :form="form"
          @click="submitForm" 
          ref="submitBtn"
          :disabled="isSendingRequest"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from "vue";
import { useRouter } from 'vue-router';
import useVuelidate from "@vuelidate/core";
import { required, url, helpers, minLength } from "@vuelidate/validators";
import PageHeading from "@/components/PageHeading.vue";
import defaultLogo from "@/assets/upload_logo.svg";
import FormLogoUpload from "@/components/FormLogoUpload.vue";
import FormInput from "@/components/FormInput.vue";
import FormPhoneInput from "@/components/FormPhoneInput.vue";
import FormColorPicker from "@/components/FormColorPicker.vue";
import FormSelect from "@/components/FormSelect.vue";
import NextButton from "@/components/NextButton.vue";
import global from "@/global";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const authStore = useAuthStore();
const submitBtn = ref(null);
const isSendingRequest = ref(false);

const fieldTouched = ref({
  phone: false,
});

const formSubmitted = ref(false);

const form = ref({
  name: "",
  address: "",
  phone: "",
  website: "",
  primaryColor: "#2D67F6",
  secondaryColor: "#0A9250",
  font: "open-sans",
  logo: null,
  phoneCountry: "us",
  isPhoneValid: false,
  phoneValidationObj: null,
});

const fonts = [
  { value: "roboto", label: "Roboto" },
  { value: "open-sans", label: "Open Sans" },
  { value: "lato", label: "Lato" },
  { value: "montserrat", label: "Montserrat" },
];

const isValidPhoneNumber = (value) => {
  if (!value) return false;
  return form.value.isPhoneValid;
};

const rules = computed(() => ({
  name: { required: helpers.withMessage("Organization Name is required", required) },
  address: { required: helpers.withMessage("Organization Address is required", required) },
  phone: {
    required: helpers.withMessage("Phone number is required", required),
    validPhoneNumber: helpers.withMessage("Please enter a valid phone number for the selected country", isValidPhoneNumber),
  },
  website: {
    required: helpers.withMessage("Website URL is required", required),
    url: helpers.withMessage("Must be a valid URL address!", helpers.regex(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9]+([\-.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,5}(:[0-9]{1,5})?(\/.*)?$/)),
  },
  font: { required: helpers.withMessage("Font selection is required", required) },
  primaryColor: { required: helpers.withMessage("Primary Color is required", required) },
  secondaryColor: { required: helpers.withMessage("Secondary Color is required", required) },
  logo: { required: helpers.withMessage("Organization Logo is required", required) },
}));

const v$ = useVuelidate(rules, form);

const logoPreview = ref(defaultLogo);
const shouldShowError = (field) => {
  return (formSubmitted.value || fieldTouched.value[field.$model]) && field.$error;
};

const handlePhoneValidation = (validation) => {
  form.value.isPhoneValid = validation.valid;
  form.value.phoneValidationObj = validation;
  v$.value.phone.$touch();
};

const handleCountryChange = (country) => {
  form.value.phoneCountry = country.iso2;
  v$.value.phone.$touch();
};

// Add logoDisplay computed property
const logoDisplay = computed(() => {
  return form.value.logo ? form.value.logo : logoPreview.value;
});

// Map form fields to API fields when sending data
const mapFormToApiData = () => {
  const formData = new FormData();
  
  // Add basic fields
  formData.append('name', form.value.name);
  formData.append('address', form.value.address);
  formData.append('phone', form.value.phone);
  formData.append('website', form.value.website);
  
  // Map color and font fields to API field names
  formData.append('background_color', form.value.primaryColor);
  formData.append('headings_color', form.value.secondaryColor);
  formData.append('body_color', '#FFFFFF'); // Default value for body color
  formData.append('font_family', form.value.font);
  
  // Handle logo upload
  if (form.value.logo instanceof File) {
    formData.append('logo', form.value.logo);
  } else if (typeof form.value.logo === 'string' && form.value.logo.startsWith('data:image')) {
    // Convert base64 to Blob and append as a file
    const blob = dataURLtoBlob(form.value.logo);
    formData.append('logo', blob, 'logo.png');
  }
  // If logo is a URL from the server, don't send it back unless changed
  
  return formData;
};

// Helper function to convert data URL to Blob
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
};

// Map API data to form fields when fetching
const mapApiDataToForm = (apiData) => {
  form.value.name = apiData.name || "";
  form.value.address = apiData.address || "";
  form.value.phone = apiData.phone || "";
  form.value.website = apiData.website || "";
  
  // Map API field names to form field names
  form.value.primaryColor = apiData.background_color || "#2D67F6";
  form.value.secondaryColor = apiData.headings_color || "#0A9250";
  form.value.font = apiData.font_family || "open-sans";
  
  // Handle logo preview if it exists
  if (apiData.logo_url) {
    logoPreview.value = apiData.logo_url;
    form.value.logo = apiData.logo_url;
  }
};

const submitForm = async () => {
  formSubmitted.value = true;
  v$.value.$touch();
  
  if (!v$.value.$error) {
    isSendingRequest.value = true;
    if (submitBtn.value) submitBtn.value.disabled = true;
    
    try {
      // Map form data to API fields and create FormData
      const formData = mapFormToApiData();
      
      // Send data to API using the upsert endpoint
      const res = await fetch(`${global.API_URL}/organizations`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`
        },
        body: formData
      });
      
      // Handle response
      const responseData = await res.json();
      
      // Navigate to next page after successful submission
      setTimeout(() => {
        isSendingRequest.value = false;
        if (submitBtn.value) submitBtn.value.disabled = false;
        router.push({ name: "about-you" });
      }, 1500);
    } catch (err) {
      console.error("API Error:", err);
      isSendingRequest.value = false;
      if (submitBtn.value) submitBtn.value.disabled = false;
    }
  }
};

// Fetch existing organization data on component mount
const fetchOrganizationData = async () => {
  try {
    const res = await fetch(`${global.API_URL}/organizations`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json"
      }
    });
    
    const data = await res.json();
    
    if (data && data.data) {
      // Map API data to form fields
      mapApiDataToForm(data.data);
    }
  } catch (err) {
    console.error("Error fetching organization data:", err);
  }
};

onMounted(async () => {
  await fetchOrganizationData();
});
</script>